import { useMutation, useQuery, useQueryClient, type QueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useUnit } from 'effector-react';
import { useEffect } from 'react';
import { queryKeys } from '~/shared/api/queryKeys';
import {
    getTraderPayinOrderServiceGetOrderCheckUrl,
    getTraderPayinOrderServiceListOrdersUrl,
    traderPayinOrderServiceAppealOrder,
    traderPayinOrderServiceAskProofsOrder,
    traderPayinOrderServiceCancelOrder,
    traderPayinOrderServiceConfirmOrder,
    traderPayinOrderServiceCreateOrder,
    traderPayinOrderServiceGetOrder,
    traderPayinOrderServiceGetOrderCheck,
    traderPayinOrderServiceListOrders,
} from '~/generated/payinBackend/order';
import {
    type PayinTraderOrderV1AppealOrderRequest,
    type PayinTraderOrderV1AskProofsOrderRequest,
    type PayinTraderOrderV1CancelOrderRequest,
    type PayinTraderOrderV1ConfirmOrderRequest,
    type PayinTraderOrderV1CreateOrderRequest,
    type TraderPayinOrderServiceGetOrderCheckParams,
    type TraderPayinOrderServiceListOrdersParams,
} from '~/generated/payinBackend/model';
import { $authUserId } from '../../../auth';
import { type Currency } from '../../../currency';
import { type PublicStatus, type Status } from '../model';

type QueryOptions<Data> = Omit<UseQueryOptions<Data, Error, Data>, 'queryFn' | 'queryKey'>;

interface OrdersParams extends Omit<
    TraderPayinOrderServiceListOrdersParams,
    'filter.statuses' | 'filter.publicStatuses' | 'filter.currency'
> {
    'filter.statuses'?: Status[];
    'filter.publicStatuses'?: PublicStatus[];
    'filter.currency'?: Currency[];
}

type OrdersListResponse = Awaited<ReturnType<typeof traderPayinOrderServiceListOrders>>;
type OrdersListQueryOptions = QueryOptions<OrdersListResponse>;

export const useOrder = (id?: string) => {
    const userId = useUnit($authUserId);

    return useQuery({
        enabled: Boolean(id && userId),
        refetchOnWindowFocus: true,
        retry: false,
        queryFn: () =>
            traderPayinOrderServiceGetOrder(
                {
                    'orderId.value': id,
                },
                {
                    context: {
                        skipErrorNotification: true,
                    },
                },
            ),
        queryKey: [queryKeys.PAYIN_ORDER, userId, id],
    });
};

export const useOrdersList = (params: OrdersParams, options: OrdersListQueryOptions = {}) => {
    return useQuery({
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        queryFn: () => traderPayinOrderServiceListOrders(params as TraderPayinOrderServiceListOrdersParams),
        queryKey: [
            queryKeys.PAYIN_ORDERS,
            getTraderPayinOrderServiceListOrdersUrl(params as TraderPayinOrderServiceListOrdersParams),
        ],
        ...options,
    });
};

type OrderChecksResponse = Awaited<ReturnType<typeof traderPayinOrderServiceGetOrderCheck>>;
interface OrderChecksQueryOptions extends QueryOptions<OrderChecksResponse> {
    onError?: VoidFunction;
}

export const useOrderChecks = (
    params: TraderPayinOrderServiceGetOrderCheckParams,
    options: OrderChecksQueryOptions = {},
) => {
    const { onError, ...queryOptions } = options;

    const queryResult = useQuery({
        queryFn: () =>
            traderPayinOrderServiceGetOrderCheck(params, {
                context: {
                    skipErrorNotification: true,
                },
            }),
        queryKey: [getTraderPayinOrderServiceGetOrderCheckUrl(params)],
        ...queryOptions,
    });

    useEffect(() => {
        if (!queryResult.data) {
            onError?.();
        }
    }, [onError, queryResult.data]);

    return queryResult;
};

interface MutationProps {
    onError?: (error: Error) => void;
    onSuccess?: () => void;
}

const staleMutationRequestOptions: RequestInit = {
    context: {
        skipErrorNotificationStatuses: [409],
    },
};

const isHttpStatusError = (error: unknown, status: number) => {
    if (typeof error !== 'object' || error === null || !('response' in error)) {
        return false;
    }

    return (error as { response?: { status?: unknown } }).response?.status === status;
};

const invalidateOrderDetail = async (queryClient: QueryClient, userId: string | null, orderId?: string) => {
    if (!userId || !orderId) {
        return;
    }

    await queryClient.invalidateQueries({
        queryKey: [queryKeys.PAYIN_ORDER, userId, orderId],
    });
};

const invalidateOrderQueries = async (queryClient: QueryClient, userId: string | null, orderId?: string) => {
    await Promise.all([
        queryClient.invalidateQueries({
            queryKey: [queryKeys.PAYIN_ORDERS],
        }),
        invalidateOrderDetail(queryClient, userId, orderId),
    ]);
};

const showStaleOrderNotification = () => {
    notifications.show({
        color: 'yellow',
        title: 'Ордер обновился',
        message: 'Состояние ордера изменилось. Данные обновлены.',
    });
};

const handleMutationError = async (
    error: Error,
    orderId: string | undefined,
    queryClient: QueryClient,
    userId: string | null,
    onError: MutationProps['onError'],
) => {
    if (isHttpStatusError(error, 409)) {
        await invalidateOrderDetail(queryClient, userId, orderId);
        showStaleOrderNotification();
    }

    onError?.(error);
};

export const useConfirmOrder = (options?: MutationProps) => {
    const queryClient = useQueryClient();
    const userId = useUnit($authUserId);

    return useMutation({
        mutationFn: async (payload: PayinTraderOrderV1ConfirmOrderRequest) => {
            return await traderPayinOrderServiceConfirmOrder(payload, staleMutationRequestOptions);
        },
        onError: (error, payload) =>
            handleMutationError(error, payload.orderId?.value, queryClient, userId, options?.onError),
        onSuccess: async (_response, payload) => {
            await invalidateOrderQueries(queryClient, userId, payload.orderId?.value);
            options?.onSuccess?.();
            notifications.show({
                color: 'green',
                title: 'Успешно',
                message: 'Получение подтверждено',
            });
        },
    });
};

export const useAppealOrder = (options?: MutationProps) => {
    const queryClient = useQueryClient();
    const userId = useUnit($authUserId);

    return useMutation({
        mutationFn: async (payload: PayinTraderOrderV1AppealOrderRequest) => {
            return await traderPayinOrderServiceAppealOrder(payload, staleMutationRequestOptions);
        },
        onError: (error, payload) =>
            handleMutationError(error, payload.orderId?.value, queryClient, userId, options?.onError),
        onSuccess: async (_response, payload) => {
            await invalidateOrderQueries(queryClient, userId, payload.orderId?.value);
            options?.onSuccess?.();
            notifications.show({
                color: 'green',
                title: 'Успешно',
                message: 'Ордер отправлен на апелляцию',
            });
        },
    });
};

export const useAskProofsOrder = (options?: MutationProps) => {
    const queryClient = useQueryClient();
    const userId = useUnit($authUserId);

    return useMutation({
        mutationFn: async (payload: PayinTraderOrderV1AskProofsOrderRequest) => {
            return await traderPayinOrderServiceAskProofsOrder(payload, staleMutationRequestOptions);
        },
        onError: (error, payload) =>
            handleMutationError(error, payload.orderId?.value, queryClient, userId, options?.onError),
        onSuccess: async (_response, payload) => {
            await invalidateOrderQueries(queryClient, userId, payload.orderId?.value);
            options?.onSuccess?.();
            notifications.show({
                color: 'green',
                title: 'Успешно',
                message: 'Запрос на подтверждение перевода отправлен',
            });
        },
    });
};

export const useCancelOrder = (options?: MutationProps) => {
    const queryClient = useQueryClient();
    const userId = useUnit($authUserId);

    return useMutation({
        mutationFn: async (payload: PayinTraderOrderV1CancelOrderRequest) => {
            return await traderPayinOrderServiceCancelOrder(payload, staleMutationRequestOptions);
        },
        onError: (error, payload) =>
            handleMutationError(error, payload.orderId?.value, queryClient, userId, options?.onError),
        onSuccess: async (_response, payload) => {
            await invalidateOrderQueries(queryClient, userId, payload.orderId?.value);
            options?.onSuccess?.();
            notifications.show({
                color: 'green',
                title: 'Успешно',
                message: 'Ордер удален',
            });
        },
    });
};

export const useCreateOrder = (options?: MutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayinTraderOrderV1CreateOrderRequest) => {
            return await traderPayinOrderServiceCreateOrder(payload);
        },
        retry: false,
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYIN_ORDERS],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Успешно',
                        message: 'Ордер создан',
                    });
                });
        },
    });
};
